import { useEffect, useRef, useState } from "react";
import { ExternalLink, Trash2, MoreHorizontal, Pencil, Bell, FileText, Download, Image as ImageIcon, ArrowRightLeft, Check, X, GripVertical, Palette, Search, RotateCcw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReminderPickerDialog } from "@/components/CalendarWidget";
import MoveToDialog from "@/components/MoveToDialog";
import { enterSubmits } from "@/lib/keys";
import ImageLightbox from "@/components/ImageLightbox";

const FALLBACK_IMG = "https://images.pexels.com/photos/7641028/pexels-photo-7641028.jpeg";

export default function Message({ item, group, onDelete, onEdit, onRemoveAttachments, onMove, titles, highlight, fileUrl, fileUrlFor, bubbleColor, onChangeBubbleColor, dragHandlers, locked }) {
  const [reminderOpen, setReminderOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isLink = item.type === "link" && item.url;
  const isFile = item.type === "file";
  // Per-thread bubble tint — with a per-item override that beats it so a
  // single urgent note can stand out in a different color.
  const bubbleBg = item.color || bubbleColor || "#2563EB";

  if (item.type === "divider") {
    return <DividerRow item={item} highlight={highlight} dragHandlers={dragHandlers} locked={locked} onDelete={onDelete} />;
  }

  return (
    <div
      className={`group flex justify-end bubble-in relative ${highlight ? "message-highlight" : ""} ${dragHandlers?.isDragging ? "opacity-40 scale-[0.98] transition-transform" : ""}`}
      data-testid={`message-${item.item_id}`}
      data-reorder-id={item.item_id}
      onDragOver={dragHandlers?.onDragOver}
      onDrop={dragHandlers?.onDrop}
      style={{ touchAction: "pan-y" }}
    >
      {dragHandlers?.dropAbove && (
        <div data-testid={`drop-indicator-above-${item.item_id}`} className="absolute left-0 right-0 top-0 h-0.5 -translate-y-1 rounded-full bg-[#3B82F6] pointer-events-none" />
      )}
      {dragHandlers?.dropBelow && (
        <div data-testid={`drop-indicator-below-${item.item_id}`} className="absolute left-0 right-0 bottom-0 h-0.5 translate-y-1 rounded-full bg-[#3B82F6] pointer-events-none" />
      )}
      <div className="flex items-end gap-1 max-w-[88%] md:max-w-[78%] min-w-0">
        {(
          <div data-snapshot-hide="true" className={`flex flex-col items-center gap-1 ${isEditing ? "invisible pointer-events-none" : ""}`} aria-hidden={isEditing || undefined}>
            {!locked && (
              <button
                type="button"
                draggable
                onDragStart={dragHandlers?.onDragStart}
                onDragEnd={dragHandlers?.onDragEnd}
                onTouchStart={dragHandlers?.onTouchStart}
                data-testid={`message-drag-${item.item_id}`}
                className="p-1.5 rounded-md hover:bg-[#1E1E1E] text-[#6B7280] hover:text-[#F3F4F6] cursor-grab active:cursor-grabbing touch-none"
                title="Drag to reorder (long-press on mobile)"
                aria-label="Drag to reorder"
              >
                <GripVertical className="h-4 w-4" />
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`message-menu-${item.item_id}`}
                  className="p-1.5 rounded-md hover:bg-[#1E1E1E] text-[#6B7280] hover:text-[#F3F4F6]"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
                {!locked && (
                  <DropdownMenuItem
                    data-testid={`edit-message-${item.item_id}`}
                    onClick={() => setIsEditing(true)}
                    className="focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
                  >
                    <Pencil className="h-4 w-4 mr-2" /> Edit
                  </DropdownMenuItem>
                )}
                {!locked && (
                  <DropdownMenuItem
                    data-testid={`move-message-${item.item_id}`}
                    onClick={() => setMoveOpen(true)}
                    className="focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-2" /> Move to…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid={`set-reminder-${item.item_id}`}
                  onClick={() => setReminderOpen(true)}
                  className="focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
                >
                  <Bell className="h-4 w-4 mr-2" /> Set as reminder
                </DropdownMenuItem>
                {onChangeBubbleColor && !locked && (
                  <DropdownMenuItem
                    data-testid={`change-color-${item.item_id}`}
                    onClick={() => onChangeBubbleColor(item.item_id)}
                    className="focus:bg-[#1E1E1E] focus:text-[#F3F4F6] cursor-pointer"
                  >
                    <Palette className="h-4 w-4 mr-2" /> Color
                  </DropdownMenuItem>
                )}
                {!locked && (
                  <DropdownMenuItem
                    data-testid={`delete-message-${item.item_id}`}
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="text-[#FCA5A5] focus:bg-[#1E1E1E] focus:text-[#FCA5A5] cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}        <div className="flex flex-col items-end gap-1 min-w-0">
          {isEditing && item.type === "note" ? (
            <InPlaceNoteEditor
              item={item}
              bubbleBg={bubbleBg}
              onCancel={() => setIsEditing(false)}
              onSave={async (newContent) => {
                if (await onEdit(item.item_id, newContent)) setIsEditing(false);
              }}
            />
          ) : isEditing ? (
            <AttachmentEditor
              item={item}
              group={group}
              bubbleBg={bubbleBg}
              fileUrl={fileUrl}
              fileUrlFor={fileUrlFor}
              onEdit={onEdit}
              onRemoveAttachments={onRemoveAttachments}
              onDone={() => setIsEditing(false)}
            />
          ) : isLink ? (
            <LinkBubble item={item} bubbleBg={bubbleBg} />
          ) : group ? (
            <PhotoGroupBubble items={group} fileUrlFor={fileUrlFor} bubbleBg={bubbleBg} />
          ) : isFile ? (
            <FileBubble item={item} fileUrl={fileUrl} bubbleBg={bubbleBg} />
          ) : (
            <div
              className="text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap [overflow-wrap:anywhere] shadow-sm max-w-full"
              style={{ backgroundColor: bubbleBg }}
            >
              {item.content}
            </div>
          )}
        </div>
      </div>
      <ReminderPickerDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        defaultText={
          isLink
            ? (item.preview?.title || item.url)
            : isFile
              ? (item.content || item.file_name || "file")
              : item.content
        }
        itemId={item.item_id}
      />
      <MoveToDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        item={item}
        titles={titles}
        currentTitleId={item.title_id}
        onSubmit={(target_title_id) => onMove(item.item_id, target_title_id)}
      />
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent
          data-testid={`delete-confirm-dialog-${item.item_id}`}
          className="bg-[#121212] border-[#27272A] text-[#F3F4F6]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              {group
                ? <>All <b className="text-[#F3F4F6]">{group.length} files</b> in this note move to the </>
                : <>This message will move to the </>}
              <b className="text-[#F3F4F6]">Trash</b> and stay there for
              <b className="text-[#F3F4F6]"> 3 days</b> — plenty of time to restore it if you change your mind. 😉
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`delete-cancel-${item.item_id}`}
              className="bg-[#1E1E1E] border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`delete-confirm-${item.item_id}`}
              onClick={() => {
                setConfirmDeleteOpen(false);
                (group || [item]).forEach((g) => onDelete(g.item_id));
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Shared contentEditable logic: the text is edited right where it is shown.
function useInPlaceEdit(initial, onSave, onCancel, allowEmpty) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const el = ref.current;
    el.textContent = initial;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const text = ref.current.innerText.replace(/\n$/, "");
    if ((!allowEmpty && !text.trim()) || busy) return;
    if (text === initial) return onCancel();
    setBusy(true);
    try { await onSave(text); } finally { setBusy(false); }
  };

  const editableProps = {
    ref,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onKeyDown: (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      else if (enterSubmits(e)) { e.preventDefault(); save(); }
    },
    onPaste: (e) => {
      e.preventDefault();
      document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    },
  };
  return { busy, save, editableProps };
}

function EditButtons({ itemId, bubbleBg, busy, onCancel, onSave }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 60);
    return () => clearTimeout(t);
  }, []);
  return (
    <div ref={ref} className="flex items-center gap-1.5 pr-1 pt-0.5 scroll-mb-24">
      <button type="button" data-testid={`inline-edit-cancel-${itemId}`} onMouseDown={(e) => e.preventDefault()} onClick={onCancel} className="p-1.5 rounded-full bg-[#1E1E1E] border border-[#3F3F46] text-[#D1D5DB] hover:text-white hover:bg-[#27272A] transition-colors" aria-label="Cancel edit">
        <X className="h-4 w-4" />
      </button>
      <button type="button" data-testid={`inline-edit-save-${itemId}`} onMouseDown={(e) => e.preventDefault()} onClick={onSave} disabled={busy} className="p-1.5 rounded-full text-white shadow-sm hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]" style={{ backgroundColor: bubbleBg }} aria-label="Save edit">
        <Check className="h-4 w-4" />
      </button>
    </div>
  );
}

function InPlaceNoteEditor({ item, bubbleBg, onSave, onCancel }) {
  const { busy, save, editableProps } = useInPlaceEdit(item.content || "", onSave, onCancel, false);
  return (
    <>
      <div
        {...editableProps}
        data-testid={`inline-edit-input-${item.item_id}`}
        className="text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap [overflow-wrap:anywhere] shadow-sm max-w-full outline-none cursor-text"
        style={{ backgroundColor: bubbleBg }}
      />
      <EditButtons itemId={item.item_id} bubbleBg={bubbleBg} busy={busy} onCancel={onCancel} onSave={save} />
    </>
  );
}

const FIRST_URL = /(https?:\/\/[^\s]*[^\s.,;:!?)"'])/i;
const CAPTION_CLS = "px-2.5 pt-1 pb-2 text-sm text-white leading-relaxed break-words whitespace-pre-wrap [overflow-wrap:anywhere]";

// Links, photos & files: the bubble (cards / photos) stays exactly as it is; only its text is editable.
function AttachmentEditor({ item, group, bubbleBg, fileUrl, fileUrlFor, onEdit, onRemoveAttachments, onDone }) {
  const isLink = item.type === "link";
  const [marked, setMarked] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const toggle = (id) => setMarked((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const removal = !isLink && onRemoveAttachments ? { marked, toggle } : undefined;
  const target = group ? (group.find((g) => g.content) || group[0]) : item;
  const initial = (() => {
    const c = target.content || "";
    if (!isLink || !item.url || c.includes(item.url)) return c;
    return c ? `${item.url}\n${c}` : item.url;
  })();
  const onSave = async (text) => {
    const url = isLink ? ((text.match(FIRST_URL) || [])[1] || "") : undefined;
    if (await onEdit(target.item_id, text, url)) onDone();
  };
  const { busy, save, editableProps } = useInPlaceEdit(initial, onSave, onDone, !isLink);
  const total = group ? group.length : 1;
  const commitRemoval = async () => {
    setConfirmOpen(false);
    setRemoving(true);
    const text = (editableProps.ref.current?.innerText || "").replace(/\n$/, "");
    try { if (await onRemoveAttachments([...marked], text)) onDone(); } finally { setRemoving(false); }
  };
  const saveAll = () => (marked.size ? setConfirmOpen(true) : save());
  const editor = (
    <div
      {...editableProps}
      data-testid={`inline-edit-input-${item.item_id}`}
      data-placeholder="Add a caption…"
      className={`${CAPTION_CLS} min-h-[2rem] outline-none cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-white/60`}
    />
  );
  return (
    <>
      {isLink ? (
        <LinkBubble item={item} bubbleBg={bubbleBg} editor={editor} />
      ) : group ? (
        <PhotoGroupBubble items={group} fileUrlFor={fileUrlFor} bubbleBg={bubbleBg} editor={editor} removal={removal} />
      ) : (
        <FileBubble item={item} fileUrl={fileUrl} bubbleBg={bubbleBg} editor={editor} removal={removal} />
      )}
      {removal && marked.size === 0 && (
        <div className="pr-1 text-[11px] text-[#9CA3AF]" data-testid={`remove-hint-${item.item_id}`}>Tap ✕ on a photo or file to remove it</div>
      )}
      <EditButtons itemId={item.item_id} bubbleBg={bubbleBg} busy={busy || removing} onCancel={onDone} onSave={saveAll} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`remove-confirm-dialog-${item.item_id}`} className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {marked.size} {marked.size === 1 ? "item" : "items"} permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              {marked.size === 1 ? "This photo/file will be deleted for good — it won't go to Trash." : "These photos/files will be deleted for good — they won't go to Trash."}
              {marked.size === total && " Your text stays in the chat as a note."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`remove-cancel-${item.item_id}`} className="bg-[#1E1E1E] border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`remove-confirm-${item.item_id}`} onClick={commitRemoval} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Edit-mode overlay on a photo/file: ✕ marks it for removal, tap again to keep it.
function RemoveMark({ id, on, row }) {
  return (
    <>
      {on && (
        <span className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-none rounded-[inherit]">
          <Trash2 className="h-5 w-5 text-[#FCA5A5]" />
        </span>
      )}
      <span
        data-testid={`remove-mark-${id}`}
        data-marked={on ? "true" : "false"}
        className={`absolute right-1.5 ${row ? "top-1/2 -translate-y-1/2" : "top-1.5"} h-6 w-6 rounded-full flex items-center justify-center shadow transition-colors ${on ? "bg-white text-black" : "bg-black/70 text-white ring-1 ring-white/30"}`}
      >
        {on ? <RotateCcw className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
      </span>
    </>
  );
}

function DividerRow({ item, highlight, dragHandlers, locked, onDelete }) {
  return (
    <div
      className={`group relative flex items-center gap-1 py-2 bubble-in ${highlight ? "message-highlight" : ""} ${dragHandlers?.isDragging ? "opacity-40" : ""}`}
      data-testid={`message-${item.item_id}`}
      data-reorder-id={item.item_id}
      style={{ touchAction: "pan-y" }}
    >
      {dragHandlers?.dropAbove && <div className="absolute left-0 right-0 top-0 h-0.5 rounded-full bg-[#3B82F6] pointer-events-none" />}
      {dragHandlers?.dropBelow && <div className="absolute left-0 right-0 bottom-0 h-0.5 rounded-full bg-[#3B82F6] pointer-events-none" />}
      {!locked && (
        <button
          type="button"
          draggable
          onDragStart={dragHandlers?.onDragStart}
          onDragEnd={dragHandlers?.onDragEnd}
          onTouchStart={dragHandlers?.onTouchStart}
          data-testid={`message-drag-${item.item_id}`}
          className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] cursor-grab active:cursor-grabbing touch-none"
          aria-label="Drag section line"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div data-testid={`divider-line-${item.item_id}`} className="flex-1 h-[3px] rounded-full bg-[#EF4444] shadow-[0_0_10px_rgba(239,68,68,0.55)]" />
      {!locked && (
        <button
          type="button"
          data-testid={`divider-delete-${item.item_id}`}
          onClick={() => onDelete(item.item_id)}
          className="p-1 rounded-full text-[#6B7280] hover:text-[#FCA5A5] hover:bg-[#1E1E1E] md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          aria-label="Remove section line"
          title="Remove line"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function LinkCard({ item, url, preview, framed }) {
  const p = preview || {};
  const host = p.site || (() => { try { return new URL(url).hostname; } catch { return ""; } })();
  const isSearch = !p.image && !!p.title && /(^| )(image )?search$/i.test(p.description || "") && /(google|bing|duckduckgo)\./i.test(host);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer external"
      onClick={(e) => e.stopPropagation()}
      data-testid={`link-card-${item.item_id}`}
      className={`block w-full max-w-md overflow-hidden bg-[#121212] transition-all cursor-pointer relative z-10 ${
        framed ? "rounded-xl border border-black/20 hover:brightness-110" : "rounded-2xl border border-[#27272A] hover:border-[#3B82F6]/60"
      }`}
    >
      <div className="h-32 w-full overflow-hidden bg-[#1E1E1E]">
        {isSearch ? (
          <div data-testid={`link-search-art-${item.item_id}`} className="h-full w-full flex flex-col items-center justify-center gap-2 px-4 bg-[#0F172A] bg-[radial-gradient(circle_at_50%_30%,rgba(37,99,235,0.28),transparent_70%)]">
            <div className="h-14 w-14 rounded-full bg-[#2563EB]/20 ring-1 ring-[#3B82F6]/40 flex items-center justify-center">
              <Search className="h-6 w-6 text-[#60A5FA]" />
            </div>
          </div>
        ) : (
          <img src={p.image || FALLBACK_IMG} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
        )}
      </div>
      <div className="p-3 space-y-1">
        <div className="flex items-center gap-1.5 text-[11px] text-[#3B82F6] truncate">
          <ExternalLink className="h-3 w-3" />
          {host}
        </div>
        <div className="text-sm font-semibold text-[#F3F4F6] line-clamp-2">{p.title || url}</div>
        {p.description && (<div className="text-xs text-[#9CA3AF] line-clamp-2">{p.description}</div>)}
      </div>
    </a>
  );
}

function LinkBubble({ item, bubbleBg = "#2563EB", editor }) {
  const framed = !!item.content || !!editor;
  const links = [{ url: item.url, preview: item.preview }, ...(item.extra_links || [])];
  const cards = (
    <div className="flex flex-col gap-1.5">
      {links.map((l) => <LinkCard key={l.url} item={item} url={l.url} preview={l.preview} framed={framed} />)}
    </div>
  );
  if (!framed) {
    return <div className="flex flex-col items-end gap-1.5 max-w-full">{cards}</div>;
  }
  return <AttachmentFrame item={item} bubbleBg={bubbleBg} editor={editor}>{cards}</AttachmentFrame>;
}

const URL_SPLIT = /(https?:\/\/[^\s]*[^\s.,;:!?)"'])/gi;
function Linkified({ text }) {
  return String(text || "").split(URL_SPLIT).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer external" onClick={(e) => e.stopPropagation()} className="underline underline-offset-2 decoration-white/60 hover:decoration-white relative z-10">
        {part}
      </a>
    ) : (
      part
    )
  );
}

/**
 * One note = one frame. When a note has both text and an attachment they share
 * a single tinted card so you can tell at a glance where one note ends and the
 * next begins.
 */
function AttachmentFrame({ item, bubbleBg, children, editor }) {
  return (
    <div
      data-testid={`note-frame-${item.item_id}`}
      className="max-w-full p-1.5 rounded-[1.25rem] rounded-br-md shadow-[0_8px_24px_-12px_rgba(0,0,0,0.8)]"
      style={{ backgroundColor: bubbleBg }}
    >
      {editor || (
        <div className={CAPTION_CLS}>
          <Linkified text={item.content} />
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Several files sent in one go: one note frame with a photo grid. Non-image
 * attachments in the batch are listed as compact file rows underneath.
 */
function PhotoGroupBubble({ items, fileUrlFor, bubbleBg, editor, removal }) {
  const [openIdx, setOpenIdx] = useState(-1);
  const caption = items.find((i) => i.content)?.content || "";
  const photos = items.filter((i) => i.is_image || (i.mime_type || "").startsWith("image/"));
  const others = items.filter((i) => !photos.includes(i));
  const cols = photos.length === 1 ? 1 : photos.length <= 4 ? 2 : 3;

  return (
    <div
      data-testid={`photo-group-${items[0].item_id}`}
      className="max-w-full p-1.5 rounded-[1.25rem] rounded-br-md shadow-[0_8px_24px_-12px_rgba(0,0,0,0.8)]"
      style={{ backgroundColor: bubbleBg }}
    >
      {editor || (caption && (
        <div className={CAPTION_CLS}>
          <Linkified text={caption} />
        </div>
      ))}
      {photos.length > 0 && (
        <div
          className="grid gap-1 rounded-xl overflow-hidden max-w-sm"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {photos.map((p, i) => {
            // Let the last photo fill the leftover cells so the grid never
            // shows a hole in its final row.
            const rem = photos.length % cols;
            const span = i === photos.length - 1 && cols > 1 && rem !== 0 ? cols - rem + 1 : 1;
            return (
              <button
                type="button"
                key={p.item_id}
                onClick={() => (removal ? removal.toggle(p.item_id) : setOpenIdx(i))}
                data-testid={`photo-group-item-${p.item_id}`}
                className={`relative overflow-hidden bg-black/40 group/ph ${cols === 1 ? "" : "aspect-square"}`}
                style={span > 1 ? { gridColumn: `span ${span}`, aspectRatio: String(span) } : undefined}
                aria-label={removal ? `Remove ${p.file_name || "photo"}` : `Open ${p.file_name || "photo"}`}
                aria-pressed={removal ? removal.marked.has(p.item_id) : undefined}
              >
                <img
                  src={fileUrlFor(p.item_id)}
                  alt={p.file_name || "photo"}
                  loading="lazy"
                  decoding="async"
                  className={`${cols === 1 ? "max-h-96 w-auto" : "h-full w-full"} object-cover transition-transform duration-300 group-hover/ph:scale-[1.04]`}
                />
                {removal && <RemoveMark id={p.item_id} on={removal.marked.has(p.item_id)} />}
              </button>
            );
          })}
        </div>
      )}
      {others.length > 0 && (
        <div className="mt-1 space-y-1">
          {others.map((f) => (
            <a
              key={f.item_id}
              href={fileUrlFor(f.item_id)}
              target="_blank"
              rel="noopener noreferrer"
              download={f.file_name || undefined}
              onClick={removal ? (e) => { e.preventDefault(); removal.toggle(f.item_id); } : undefined}
              data-testid={`photo-group-file-${f.item_id}`}
              className={`relative flex items-center gap-2 rounded-xl bg-[#121212] px-2.5 py-2 hover:brightness-110 transition-all ${removal ? "pr-9" : ""}`}
            >
              <FileText className="h-4 w-4 text-[#3B82F6] shrink-0" />
              <span className="text-xs text-[#F3F4F6] truncate flex-1">{f.file_name || "file"}</span>
              {removal ? <RemoveMark id={f.item_id} on={removal.marked.has(f.item_id)} row /> : <Download className="h-3.5 w-3.5 text-[#9CA3AF] shrink-0" />}
            </a>
          ))}
        </div>
      )}
      <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] text-white/70">
        {photos.length > 0 && `${photos.length} photo${photos.length === 1 ? "" : "s"}`}
        {photos.length > 0 && others.length > 0 && " · "}
        {others.length > 0 && `${others.length} file${others.length === 1 ? "" : "s"}`}
      </div>
      {openIdx >= 0 && photos[openIdx] && (
        <ImageLightbox
          open
          images={photos.map((p) => ({
            src: fileUrlFor(p.item_id),
            alt: p.file_name || "photo",
            downloadName: p.file_name,
          }))}
          index={openIdx}
          onClose={() => setOpenIdx(-1)}
        />
      )}
    </div>
  );
}

function FileBubble({ item, fileUrl, bubbleBg = "#2563EB", editor, removal }) {
  const isImage = item.is_image || (item.mime_type || "").startsWith("image/");
  const sizeKb = item.size ? (item.size / 1024).toFixed(1) : null;
  const openInNew = (e) => {
    e.stopPropagation();
    if (removal) { e.preventDefault(); removal.toggle(item.item_id); }
  };
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const framed = !!item.content || !!editor;
  const mark = removal && <RemoveMark id={item.item_id} on={removal.marked.has(item.item_id)} row={!isImage} />;

  const card = isImage ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (removal) removal.toggle(item.item_id); else setLightboxOpen(true); }}
      data-testid={`file-image-${item.item_id}`}
      className={`relative block max-w-sm overflow-hidden bg-[#121212] transition-all ${
        framed ? "rounded-xl" : "rounded-2xl border border-[#27272A] hover:border-[#3B82F6]/60"
      }`}
      aria-label={removal ? "Remove image" : "Open image"}
    >
      <img
        src={fileUrl}
        alt={item.file_name || "image"}
        className="max-h-96 w-auto object-contain bg-black"
        loading="lazy"
        decoding="async"
      />
      {mark}
    </button>
  ) : (
    <a
      href={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      download={item.file_name || undefined}
      onClick={openInNew}
      data-testid={`file-card-${item.item_id}`}
      className={`relative flex items-center gap-3 w-full max-w-md bg-[#121212] transition-all p-3 ${removal ? "pr-10" : ""} ${
        framed ? "rounded-xl border border-black/20 hover:brightness-110" : "rounded-2xl border border-[#27272A] hover:border-[#3B82F6]/60"
      }`}
    >
      <div className="h-11 w-11 rounded-lg bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-[#3B82F6]">
        {(item.mime_type || "").startsWith("image/") ? (<ImageIcon className="h-5 w-5" />) : (<FileText className="h-5 w-5" />)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#F3F4F6] truncate">{item.file_name || "file"}</div>
        <div className="text-[11px] text-[#9CA3AF] truncate">{(item.mime_type || "file")}{sizeKb ? ` · ${sizeKb} KB` : ""}</div>
      </div>
      {removal ? mark : <Download className="h-4 w-4 text-[#9CA3AF] shrink-0" />}
    </a>
  );

  const lightbox = isImage && (
    <ImageLightbox
      open={lightboxOpen}
      src={fileUrl}
      alt={item.file_name || "image"}
      downloadName={item.file_name}
      onClose={() => setLightboxOpen(false)}
    />
  );

  if (!framed) {
    return (
      <div className="flex flex-col items-end gap-1.5 max-w-full">
        {card}
        {lightbox}
      </div>
    );
  }
  return (
    <AttachmentFrame item={item} bubbleBg={bubbleBg} editor={editor}>
      {card}
      {lightbox}
    </AttachmentFrame>
  );
}
